import {useEffect, useMemo} from 'react'

import {
  setForm,
  updateForm,
  removeForm,
  useSelector,
  onlyIfForm,
  getState,
} from '../../../store.js'
import {
  LineLayouts,
  BarcodeTypeOptions,
  OrderReferenceKeyOptions,
} from '../../../common/constants/LineLabels.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import ButtonSendToPrinter, {
  sendToPrintNode,
  startSendingToPrintNode,
} from '../../../common/components/Button/ButtonSendToPrinter.js'
import Zelect from '../../../common/components/Zelect.js'
import {orderLinePrinterIDSelector} from '../../../data/printerSettings.js'
import {formsSelector} from '../../../redux/selectors/ui/forms.js'
import {LAYOUT_LINE, layoutsByTypeSortedByName} from '../../../data/layout.js'
import Checkbox from '../../../common/components/Checkbox.js'
import {saveUserSettings, userSettingSelector} from '../../../data/me.js'
import {
  Count,
  IfPlural,
  IfSingle,
  plural,
  Plural,
  PluralBlock,
} from '../../../common/components/Plural.js'
import {abodeRenderPOST} from '../../../common/abode.js'
import {openFileInTab} from '../../../common/openInTab.js'
import {openSyncLoadingTab} from '../../AbodeForm/abodeFormFunctions.js'
import {allOrdersSelector, orderLineSelector} from '../../../data/orders.js'
import {createBatchAction} from '../../../data/batches.js'
import {checkRunningTasks} from '../../../data/tasks.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {printerNameSelector} from '../../../data/printNode.js'
import {BATCH_CREATE_DOCUMENT_COMMENT} from '../../../common/constants/Comments.js'
import {productsSelector} from '../../../data/products.js'
import {batchReferenceIDSelector} from '../orderListSelectors.js'

export const MODAL_FORM = 'order_line_label_modal'

const SORT_OPTIONS = [
  {
    value: 'reference_id',
    display: 'Order Number',
    sort: ['reference_id', 'sku'],
  },
  {value: 'sku', display: 'SKU', sort: ['sku']},
]

export function orderLineLabelModalSettingsSelector(state) {
  return userSettingSelector(state, {
    setting: MODAL_FORM,
    defaultValue: {
      layout_name: LineLayouts[0].value,
      sort: 'reference_id',
      print_each_quantity: true,
      show_kit_components: false,
      reference_id_barcode_key: 'reference_id',
      reference_id_barcode_type: 'CODE128',
      sku_barcode_type: 'CODE128',
      layout_id: null,
    },
  })
}

export function showOrderLineLabelModal({
  orderNumbers,
  lineIDs,
  batchReferenceIDs,
}) {
  setForm(MODAL_FORM, {
    orderNumbers,
    lineIDs,
    batchReferenceIDs,
    ...orderLineLabelModalSettingsSelector(getState()),
  })
}

function buildItem(order, line, product) {
  return {
    reference_id: order.order_number,
    barcode: order.barcode,
    sku: line.sku,
    product_name: line.product_name,
    quantity: line.quantity,
    kit_components: product.kit_components.map(({sku, name, quantity}) => ({
      sku,
      name,
      quantity,
    })),
  }
}

function itemsSelector(state) {
  const {orderNumbers, lineIDs, batchReferenceIDs} = modalFormSelector(state)

  if (batchReferenceIDs?.length) {
    return [[], []]
  }

  const allOrders = allOrdersSelector(state)
  const products = productsSelector(state)

  const items = []
  const allItems = []
  if (!lineIDs?.length) {
    for (const orderNumber of orderNumbers) {
      const order = allOrders[orderNumber]

      for (const line of order.lines) {
        const product = products[line.sku]

        if (product?.needs_order_line_labels) {
          items.push(buildItem(order, line, product))
        }
        allItems.push(line.sku)
      }
    }
  } else {
    const orderNumber = orderNumbers[0]
    for (const lineID of lineIDs) {
      const order = allOrders[orderNumber]
      const line = orderLineSelector(state, {
        orderNumber,
        orderLineID: lineID,
      })

      const product = products[line.sku]

      if (product?.needs_order_line_labels) {
        items.push(buildItem(order, line, product))
      }
      allItems.push(line.sku)
    }
  }

  return [items, allItems]
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

function saveOrderLineLabelModalSettings() {
  const form = modalFormSelector(getState())

  if (!form) {
    return
  }

  const {
    layout_name,
    sort,
    print_each_quantity,
    show_kit_components,
    reference_id_barcode_key,
    reference_id_barcode_type,
    sku_barcode_type,
  } = form

  saveUserSettings({
    [MODAL_FORM]: {
      layout_name,
      sort,
      print_each_quantity,
      show_kit_components,
      reference_id_barcode_key,
      reference_id_barcode_type,
      sku_barcode_type,
    },
  })
}

export function closeModal(outcome) {
  if (outcome) {
    saveOrderLineLabelModalSettings()
  }

  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export function orderLineLabelParamsSelector(state) {
  const {
    layout_name,
    sort,
    print_each_quantity,
    show_kit_components,
    reference_id_barcode_key,
    reference_id_barcode_type,
    sku_barcode_type,
    layout_id,
  } = modalFormSelector(state)
  const [items] = itemsSelector(state)
  const batch_reference_id = batchReferenceIDSelector(state)

  const params = {
    file_name: 'line_labels.pdf',
    render: [
      'CardLayout',
      {
        layout_name,
        children: [
          [
            'LineLabel',
            {
              sort: SORT_OPTIONS.find(({value}) => value === sort).sort,
              print_each_quantity,
              show_kit_components,
              reference_id_barcode_key,
              reference_id_barcode_type,
              sku_barcode_type,
              batch_reference_id,
              items,
            },
          ],
        ],
      },
    ],
  }

  if (layout_id) {
    params.layout_id = layout_id
  }

  return params
}

async function renderOrderLineLabels(tab) {
  const params = orderLineLabelParamsSelector(getState())

  const file = await abodeRenderPOST(params)

  if (tab) {
    openFileInTab(file, tab)

    closeModal(true)
  }

  return file
}

function openOrderLineLabelsInTab() {
  const tab = openSyncLoadingTab()

  if (!tab) {
    return
  }

  renderOrderLineLabels(tab)
}

async function sendOrderLineLabelsToPrintNode({
  canPrint,
  printButtonFormKey,
  printerID,
  documentCount,
}) {
  if (!canPrint) {
    return
  }

  const {batchReferenceIDs} = modalFormSelector(getState())

  if (batchReferenceIDs?.length) {
    createBatchLineLabels(printerID)
  } else {
    startSendingToPrintNode(printButtonFormKey)

    const file = await renderOrderLineLabels()

    await sendToPrintNode({
      formKey: printButtonFormKey,
      pdfFile: file,
      pdfName: file.name,
      printerID,
      documentCount,
    })
  }

  closeModal(true)
}

export function batchCreateLineLabelsPayloadSelector(state, {printerID} = {}) {
  const {
    batchReferenceIDs,
    layout_name,
    sort,
    print_each_quantity,
    show_kit_components,
    reference_id_barcode_key,
    reference_id_barcode_type,
    sku_barcode_type,
    layout_id,
  } = modalFormSelector(state)

  const params = {
    action: {type: 'mult-stage-processing'},
    actions: [],
  }

  const comment = {
    type: BATCH_CREATE_DOCUMENT_COMMENT,
    docs: ['line_label'],
  }

  params.actions.push({
    type: 'render_line_label',
    stop_on_error: true,
    data: {
      layout_id,
      card_layout_props: {
        layout_name,
      },
      line_label_props: {
        sort: SORT_OPTIONS.find(({value}) => value === sort).sort,
        print_each_quantity,
        show_kit_components,
        reference_id_barcode_key,
        reference_id_barcode_type,
        sku_barcode_type,
      },
    },
  })

  if (printerID) {
    params.actions.push({
      type: 'send_to_printer',
      stop_on_error: true,
      data: {
        content_type: 'pdf_uri',
        printer_id: printerID,
        content_url: '$previous.output.-1.cache_url',
      },
    })

    comment.printer_name = printerNameSelector(state, {
      printerID,
    })
  }

  const toast = plural(
    batchReferenceIDs,
  )`A task was started to create line labels for ${Count} batch${['es']}.`

  return [{params, comment: JSON.stringify(comment)}, toast]
}

export async function createBatchLineLabels(printerID) {
  const {batchReferenceIDs} = modalFormSelector(getState())

  try {
    updateModalForm({isSaving: true})

    const [payload, toast] = batchCreateLineLabelsPayloadSelector(getState(), {
      printerID,
    })

    await Promise.all(
      batchReferenceIDs.map((referenceID) =>
        createBatchAction(referenceID, payload),
      ),
    )

    checkRunningTasks()

    showMessageToast(toast)

    closeModal(true)
  } catch (err) {
    updateModalForm({
      serverError: `Error creating documents for batches: ${
        err.message || err.error_message
      }`,
      isSaving: false,
    })
  }
}

function updateLayoutID(layout_id) {
  updateModalForm({layout_id: Number(layout_id) || null})
}

function OrderLineLabelModal({form}) {
  const batchMode = form.batchReferenceIDs?.length
  const orderLinePrinterID = useSelector(orderLinePrinterIDSelector)
  const lineLayouts = useSelector(layoutsByTypeSortedByName)[LAYOUT_LINE] || []
  const lineLayoutOptions = useMemo(
    () => [
      {value: '', display: 'Standard'},
      ...lineLayouts.map((layout) => ({
        value: layout.id,
        display: layout.name,
      })),
    ],
    [lineLayouts],
  )
  const [items, allItems] = useSelector(itemsSelector)
  const needsNotice = batchMode || items.length !== allItems.length
  const hasItemError = !batchMode && items.length === 0

  useEffect(() => {
    if (form.layoutID && !lineLayouts.find(({id}) => id === form.layoutID)) {
      updateLayoutID(null)
    }
  }, [form.layoutID])

  return (
    <ConfirmModal
      title={batchMode ? 'Create Line Labels' : 'Print Line Labels'}
      modalSize="sm"
      onConfirm={batchMode ? createBatchLineLabels : openOrderLineLabelsInTab}
      onCancel={() => closeModal(false)}
      confirmText={batchMode ? 'Create' : 'View/Print'}
      cancelText="Cancel"
      middleButtons={
        <div className="margin-right-10">
          <ButtonSendToPrinter
            className="margin-bottom-5 meta-linelabelmodal-button-instaprint"
            title="Line Label"
            printerID={orderLinePrinterID}
            documentCount={1}
            onClick={({canPrint, printButtonFormKey}) =>
              sendOrderLineLabelsToPrintNode({
                canPrint,
                printButtonFormKey,
                printerID: orderLinePrinterID,
                documentCount: 1,
              })
            }
            isDisabled={hasItemError}
          />
        </div>
      }
      error={form.serverError}
      isSaving={form.isSaving}
      isDisabled={hasItemError}
    >
      <dl className="list">
        <dt className="list__title--label-success lh-md margin-top-0 margin-bottom-15">
          {form.lineIDs?.length > 0 ? (
            <PluralBlock array={form.lineIDs}>
              <Count /> <Plural word="Line" /> for Order {form.orderNumbers[0]}
            </PluralBlock>
          ) : form.orderNumbers?.length > 0 ? (
            <PluralBlock array={form.orderNumbers}>
              All Lines for <IfSingle>Order {form.orderNumbers[0]}</IfSingle>
              <IfPlural>
                <Count /> Orders
              </IfPlural>
            </PluralBlock>
          ) : form.batchReferenceIDs?.length > 0 ? (
            <PluralBlock array={form.batchReferenceIDs}>
              All Lines for{' '}
              <IfSingle>Batch {form.batchReferenceIDs[0]}</IfSingle>
              <IfPlural>
                <Count /> Batches
              </IfPlural>
            </PluralBlock>
          ) : null}
        </dt>
        {needsNotice && (
          <>
            <dd className="list__item fs-00 lh-md">
              Some products won’t get labels. To include them, set{' '}
              <strong>Include Order Line Labels?</strong> to{' '}
              <strong>Yes</strong> on their Product Detail page.
            </dd>
            <dd className="list__item fs-00 lh-md margin-bottom-25">
              <a
                className="btn--link darker border-underline"
                href="https://support.ordoro.com/how-do-i-print-order-line-labels/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more about Line Labels
              </a>{' '}
              →
            </dd>
          </>
        )}
        <dd className="list__item margin-bottom-15">
          <Zelect
            label="Size"
            id="layout_name"
            value={form.layout_name}
            onChange={({value}) => updateModalForm({layout_name: value})}
            options={LineLayouts}
          />
        </dd>
        {lineLayouts.length > 0 && (
          <dd className="list__item margin-bottom-15">
            <Zelect
              label="Layout"
              id="layout_id"
              value={form.layout_id || ''}
              onChange={({value}) => updateLayoutID(value)}
              options={lineLayoutOptions}
            />
          </dd>
        )}
        <dd className="list__item margin-bottom-15">
          <Zelect
            label="Sort"
            id="sort"
            value={form.sort}
            onChange={({value}) => updateModalForm({sort: value})}
            options={SORT_OPTIONS}
          />
        </dd>
        <dd className="list__item margin-bottom-15">
          <Zelect
            label="Reference Barcode Type"
            id="reference_id_barcode_type"
            value={form.reference_id_barcode_type}
            onChange={({value}) =>
              updateModalForm({reference_id_barcode_type: value})
            }
            options={BarcodeTypeOptions}
          />
        </dd>
        <dd className="list__item margin-bottom-15">
          <Zelect
            label="Reference Barcode Property"
            id="reference_id_barcode_key"
            value={form.reference_id_barcode_key}
            onChange={({value}) =>
              updateModalForm({reference_id_barcode_key: value})
            }
            options={OrderReferenceKeyOptions}
          />
        </dd>
        <dd className="list__item margin-bottom-15">
          <Zelect
            label="SKU Barcode Type"
            id="sku_barcode_type"
            value={form.sku_barcode_type}
            onChange={({value}) => updateModalForm({sku_barcode_type: value})}
            options={BarcodeTypeOptions}
          />
        </dd>
        <dd className="list__item margin-bottom-15">
          <Checkbox
            label="Print Each Quantity"
            id="print_each_quantity"
            labelClassName="margin-bottom-15"
            checked={form.print_each_quantity}
            onChange={(print_each_quantity) =>
              updateModalForm({print_each_quantity})
            }
          />
        </dd>
        <dd className="list__item">
          <Checkbox
            label="Include Kit Components"
            id="show_kit_components"
            labelClassName="margin-bottom-15"
            checked={form.show_kit_components}
            onChange={(show_kit_components) =>
              updateModalForm({show_kit_components})
            }
          />
        </dd>
      </dl>
    </ConfirmModal>
  )
}

export default onlyIfForm(OrderLineLabelModal, modalFormSelector)
